import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/ui/Icons';
import useReveal from '../components/ui/Reveal';
import COMPANY, { ROUTES } from '../config/company';
import { TRAILERS } from './Trailers';

import heroPoster from '../assets/Images/heroTruckImage.png';
import heroVideo from '../assets/Images/HomeHeroVideo.mp4';
import yardImage from '../assets/Images/DivImage1.png';
import roadImage from '../assets/Images/DivImage2.png';

import p1 from '../assets/Images/PartnersImage1.png';
import p2 from '../assets/Images/PartnersImage2.png';
import p3 from '../assets/Images/PartnersImage3.png';
import p4 from '../assets/Images/PartnersImage4.png';
import p5 from '../assets/Images/PartnersImage5.png';
import p6 from '../assets/Images/PartnersImage6.png';

const STATS = [
  ['3', 'Trailer types', 'Dry van, reefer, flatbed'],
  ['53′', 'Standard length', 'Air-ride, DOT-inspected'],
  ['0', 'Mileage fees', 'Transparent flat pricing'],
  ['24/7', 'Support', 'Maintenance covered*'],
];

const ADVANTAGES = [
  ['shield', 'Top-quality equipment', 'Every trailer is DOT-inspected and road-ready. Filter by suspension type, food grade, model year and tire rating.'],
  ['calendar', 'Hassle-free booking', 'Book for same-day pickup or plan ahead. Reserve online, confirm by phone, hook up and go.'],
  ['layers', 'Extensive selection', 'From a single trailer to large drop-and-hook projects, we size the fleet to your lane.'],
  ['bolt', 'Flexible reservations', 'No long-term contracts. Reserve, extend or return on your terms as your loads change.'],
  ['dollar', 'Affordable pricing', 'Flat rates with no mileage fees, typically less than half the cost of comparable leases.'],
  ['wrench', 'Maintenance covered', 'We cover trailer maintenance for the life of the rental. Tires are the only exclusion.'],
];

const STEPS = [
  ['Reserve', 'Choose a dry van, reefer or flatbed and tell us your pickup date. Carriers sign up once with MC, USDOT and insurance.'],
  ['Pick up', 'Head to the facility we assign you, find your trailer, hook up and leave. No waiting at an office.'],
  ['Load & go', 'Run your loads. Extend, swap or return whenever your schedule changes.'],
];

const BRANDS = [
  [p1, 'XTRA Lease'],
  [p2, 'Schneider'],
  [p3, 'Utility Trailer'],
  [p4, 'Navajo Express'],
  [p5, 'Thermo King'],
  [p6, 'Wabash National'],
];

export default function Home() {
  useReveal();
  return (
    <>
      {/* HERO */}
      <section className="relative isolate min-h-[92vh] overflow-hidden bg-navy-900 text-white">
        <video
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={heroPoster}
          aria-hidden="true"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-navy-950/95 via-navy-900/80 to-navy-900/40"
          aria-hidden="true"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy-900 via-transparent" aria-hidden="true" />

        <div className="shell flex min-h-[92vh] flex-col justify-center pb-20 pt-36 sm:pt-40">
          <div className="max-w-3xl">
            <div className="eyebrow mb-5 animate-fadeUp text-gold-300">
              {COMPANY.brand} · Trailer rentals
            </div>
            <h1 className="h1 animate-fadeUp text-white" style={{ animationDelay: '0.08s' }}>
              Leading the future of
              <br />
              <span className="text-gold-300">freight solutions.</span>
            </h1>
            <p
              className="mt-6 max-w-xl animate-fadeUp text-lg leading-relaxed text-navy-100 sm:text-xl"
              style={{ animationDelay: '0.18s' }}
            >
              DOT-inspected dry van, reefer and flatbed trailers for carriers and owner-operators. Flexible terms,
              transparent pricing, pickup when you need it. <strong className="text-white">{COMPANY.tagline}</strong>
            </p>
            <div className="mt-9 flex flex-wrap gap-3 animate-fadeUp" style={{ animationDelay: '0.28s' }}>
              <Link to={ROUTES.trailers} className="btn-primary">
                View trailers <Icon name="arrowRight" className="h-4 w-4" />
              </Link>
              <a href={COMPANY.email} className="btn-outline-light">
                <Icon name="mail" className="h-4 w-4" /> {COMPANY.emailDisplay}
              </a>
            </div>
          </div>

          <dl className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 backdrop-blur lg:max-w-4xl lg:grid-cols-4">
            {STATS.map(([big, label, sub]) => (
              <div key={label} className="bg-navy-900/70 px-5 py-5">
                <dd className="font-display text-4xl font-extrabold text-gold-300">{big}</dd>
                <dt className="mt-1 text-[0.85rem] font-bold uppercase tracking-wider text-white">{label}</dt>
                <p className="text-[0.8rem] text-navy-200">{sub}</p>
              </div>
            ))}
          </dl>
        </div>
        <div className="diagonal-stripe absolute inset-x-0 bottom-0 h-2" aria-hidden="true" />
      </section>

      {/* TRAILERS */}
      <section className="py-20 sm:py-28">
        <div className="shell">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3">Available trailers</div>
              <h2 className="h2">Pick the trailer. We handle the rest.</h2>
            </div>
            <Link to={ROUTES.trailers} className="btn-outline shrink-0">
              All trailers &amp; pricing
            </Link>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TRAILERS.map((t, i) => (
              <article
                key={t.id}
                className="card reveal group overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                style={{ '--reveal-delay': `${i * 90}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-ink-100">
                  <img
                    src={t.image}
                    alt={`${t.name} trailer`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute left-4 top-4 rounded bg-navy-900 px-2.5 py-1 text-[0.72rem] font-bold uppercase tracking-wider text-gold-300">
                    {t.badge}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="h3">{t.name}</h3>
                    <div className="text-right">
                      <div className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-500">From</div>
                      <div className="font-display text-2xl font-extrabold text-navy-900">{t.price}</div>
                      <div className="text-[0.72rem] text-ink-500">per month</div>
                    </div>
                  </div>
                  <p className="mt-3 text-[0.92rem] leading-relaxed text-ink-600">{t.short}</p>
                  <div className="mt-5 flex gap-3">
                    <Link to={ROUTES.signup} className="btn-dark flex-1 px-4 py-2.5 text-[0.82rem]">
                      Book now
                    </Link>
                    <Link
                      to={`${ROUTES.trailers}#${t.id}`}
                      className="btn-outline px-4 py-2.5 text-[0.82rem]"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="texture-dark bg-navy-900 py-20 text-white sm:py-28">
        <div className="shell grid items-center gap-12 lg:grid-cols-2">
          <div className="reveal">
            <div className="eyebrow mb-3 text-gold-300">How it works</div>
            <h2 className="h2 text-white">Seamless pickups, completely hassle-free.</h2>
            <p className="mt-5 text-lg leading-relaxed text-navy-100">
              Go to the facility we assign you, find your trailer, hook up and go. No going up and down the offices,
              no paperwork at the gate.
            </p>
            <ol className="mt-8 space-y-6">
              {STEPS.map(([title, body], i) => (
                <li key={title} className="flex gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gold-300 font-display text-xl font-extrabold text-navy-900">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">{title}</h3>
                    <p className="mt-1 text-[0.95rem] leading-relaxed text-navy-200">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to={ROUTES.signup} className="btn-primary mt-9">
              Start your reservation <Icon name="arrowRight" className="h-4 w-4" />
            </Link>
          </div>
          <div className="reveal relative" style={{ '--reveal-delay': '120ms' }}>
            <img
              src={yardImage}
              alt="Trailers lined up in a freight yard"
              className="w-full rounded-2xl object-cover shadow-lift"
              loading="lazy"
            />
            <img
              src={roadImage}
              alt="Tractor-trailer on the highway"
              className="absolute -bottom-8 -left-6 hidden w-2/5 rounded-xl border-4 border-navy-900 object-cover shadow-lift sm:block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-ink-50 py-20 sm:py-28">
        <div className="shell">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Why Bitnex Freight LLC</div>
            <h2 className="h2">The most innovative, driver-friendly rental on the road.</h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ADVANTAGES.map(([icon, title, body], i) => (
              <div
                key={title}
                className="card reveal p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                style={{ '--reveal-delay': `${(i % 3) * 80}ms` }}
              >
                <span className="mb-5 grid h-12 w-12 place-items-center rounded-lg bg-navy-900 text-gold-300">
                  <Icon name={icon} className="h-5 w-5" />
                </span>
                <h3 className="font-display text-xl font-bold">{title}</h3>
                <p className="mt-2 text-[0.92rem] leading-relaxed text-ink-600">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[0.8rem] text-ink-500">*Trailer maintenance is covered during the rental. Trailer tires are excluded.</p>
        </div>
      </section>

      {/* BRANDS */}
      <section className="border-y border-ink-200 bg-white py-14">
        <div className="shell">
          <p className="text-center text-[0.72rem] font-bold uppercase tracking-[0.2em] text-ink-500">
            Equipment &amp; industry brands in our fleet and network
          </p>
          <div className="mt-8 grid grid-cols-3 items-center gap-8 md:grid-cols-6">
            {BRANDS.map(([src, alt]) => (
              <img
                key={alt}
                src={src}
                alt={alt}
                className="mx-auto max-h-12 w-auto object-contain opacity-70 grayscale transition hover:opacity-100 hover:grayscale-0"
                loading="lazy"
              />
            ))}
          </div>
          <p className="mt-6 text-center text-[0.72rem] text-ink-400">
            Logos are trademarks of their respective owners and indicate equipment makes and carriers we work with. No
            endorsement is implied.
          </p>
        </div>
      </section>

      {/* SMS / CONTACT CTA */}
      <section className="py-20 sm:py-28">
        <div className="shell grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          <div className="card reveal texture-dark bg-navy-900 p-8 text-white sm:p-10">
            <div className="eyebrow mb-3 text-gold-300">Talk to a person</div>
            <h2 className="h2 text-white">Need a trailer this week?</h2>
            <p className="mt-4 max-w-xl text-lg text-navy-100">
              Call us, email us or send the form. A Bitnex Freight LLC representative replies {COMPANY.responseTime.toLowerCase()}.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={COMPANY.email} className="btn-primary">
                <Icon name="mail" className="h-4 w-4" /> {COMPANY.emailDisplay}
              </a>
              <Link to={ROUTES.contact} className="btn-outline-light">
                Contact form
              </Link>
            </div>
          </div>
          <div className="card reveal p-8 sm:p-10" style={{ '--reveal-delay': '120ms' }}>
            <span className="mb-5 grid h-12 w-12 place-items-center rounded-lg bg-gold-300 text-navy-900">
              <Icon name="message" className="h-5 w-5" />
            </span>
            <h2 className="h3">Get pickup &amp; reservation updates by text</h2>
            <p className="mt-3 text-[0.92rem] leading-relaxed text-ink-600">
              Opt in on our SMS consent page to receive customer care texts from {COMPANY.legalName} (reservation
              confirmations, pickup and return reminders, account and billing notices, customer-care replies) and,
              if you separately choose, marketing offers. Message frequency may vary; message and data rates may apply.
              Reply STOP to opt out or HELP for help. Consent is optional and not a condition of purchase.
            </p>
            <Link to={ROUTES.smsOptIn} className="btn-dark mt-6">
              SMS opt-in <Icon name="arrowRight" className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
